import { ApprovalStrategy } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'

/** 1 承認段階のフロントエンド表現 */
export type StageLocal = {
  strategy: ApprovalStrategy
  /** approver の member_id 文字列リスト */
  approverMemberIds: string[]
}

/** 初期 StageLocal を作る */
export function makeEmptyStage(): StageLocal {
  return {
    strategy: ApprovalStrategy.ALL,
    approverMemberIds: [],
  }
}

/** 承認者が 0 人の段がある (AC-APF-021 / 071 の一部。 StageEditModal の「完了」可否にも使う) */
export function hasStageWithoutApprovers(
  stages: ReadonlyArray<Pick<StageLocal, 'approverMemberIds'>>,
): boolean {
  return stages.some((s) => s.approverMemberIds.length === 0)
}

/**
 * 要承認なのに承認者を決めきっていない (段階 0、 または承認者 0 人の段がある)
 * (AC-APF-021 / 071)。 承認モードの型は画面ごとに違うので「要承認か」を受け取る。
 */
export function lacksApprovers(
  requiresApproval: boolean,
  stages: ReadonlyArray<Pick<StageLocal, 'approverMemberIds'>>,
): boolean {
  return (
    requiresApproval &&
    (stages.length === 0 || hasStageWithoutApprovers(stages))
  )
}

/** 承認者は段階内で順序を持たない (AC-APF-018) ので、 集合として等しいかを見る。 */
function sameApproverSet(a: readonly string[], b: readonly string[]): boolean {
  const setA = new Set(a)
  const setB = new Set(b)
  return setA.size === setB.size && [...setA].every((id) => setB.has(id))
}

/**
 * 一括の承認段階設定 UI の初期値 (AC-APF-075)。
 * 全セルが 1 段以上の同じ承認段階 (段階の数と、 段階ごとの承認戦略・承認者の集合が
 * すべて同じ) を持つ時だけそれを複製して返し、 それ以外 (セルによって異なる / 承認段階が
 * 無いセルがある / セルが無い) は空の 1 段階を返す。
 */
export function commonInitialStages(
  cellsStages: ReadonlyArray<readonly StageLocal[]>,
): StageLocal[] {
  const first = cellsStages[0] ?? []
  if (first.length === 0) return [makeEmptyStage()]
  const allSame = cellsStages.every(
    (stages) =>
      stages.length === first.length &&
      first.every((s, i) => {
        const other = stages[i]
        return (
          other !== undefined &&
          other.strategy === s.strategy &&
          sameApproverSet(other.approverMemberIds, s.approverMemberIds)
        )
      }),
  )
  if (!allSame) return [makeEmptyStage()]
  return first.map((s) => ({
    ...s,
    approverMemberIds: [...s.approverMemberIds],
  }))
}
